// Ozy
// Music Player - Audio management, waveform visualization, and state persistence
// Includes ID3 tag parsing for automatic metadata extraction

(function() {
  'use strict';

  const STORAGE_KEY = 'seraphinaGreyMusicPlayer';
  
  // State management
  const state = {
    audio: null,
    audioContext: null,
    analyser: null,
    isPlaying: false,
    isMinimized: false, // Default to full bar, not minimized
    isMuted: false,
    volume: 0.8,
    currentTime: 0,
    duration: 0,
    src: '',
    trackTitle: '',
    artistName: '',
    albumName: '',
    year: '',
    articleUrl: '',
    coverImage: '',
    activePlayer: null, // 'inline' or 'footer'
    waveformData: null,
    id3Loaded: false
  };

  // ID3 Tag Parser - extracts metadata and album art from MP3 files
  const ID3Parser = {
    // ID3v2 frame IDs
    frameIds: {
      TIT2: 'title',
      TPE1: 'artist',
      TALB: 'album',
      TYER: 'year',
      TDRC: 'year', // ID3v2.4
      APIC: 'picture'
    },

    async parse(url) {
      try {
        // Fetch first 256KB of the file (enough for ID3 headers + embedded art)
        const response = await fetch(url, {
          headers: { Range: 'bytes=0-262144' }
        });
        
        if (!response.ok) return null;
        
        const buffer = await response.arrayBuffer();
        const data = new Uint8Array(buffer);
        
        // Check for ID3v2 header
        if (this.isID3v2(data)) {
          return this.parseID3v2(data);
        }
        
        return null;
      } catch (e) {
        console.warn('ID3 parsing failed:', e);
        return null;
      }
    },

    isID3v2(data) {
      // ID3v2 starts with "ID3"
      return data[0] === 0x49 && data[1] === 0x44 && data[2] === 0x33;
    },

    parseID3v2(data) {
      const version = data[3];
      const size = this.syncsafeInt(data, 6);
      
      const result = {
        title: '',
        artist: '',
        album: '',
        year: '',
        picture: null // Will be a data URL if found
      };

      let offset = 10;
      const end = Math.min(10 + size, data.length);

      while (offset < end - 10) {
        // Read frame header
        const frameId = String.fromCharCode(data[offset], data[offset + 1], data[offset + 2], data[offset + 3]);
        
        if (frameId === '\0\0\0\0' || !frameId.match(/^[A-Z0-9]{4}$/)) break;
        
        const frameSize = version >= 4 
          ? this.syncsafeInt(data, offset + 4)
          : (data[offset + 4] << 24) | (data[offset + 5] << 16) | (data[offset + 6] << 8) | data[offset + 7];
        
        if (frameSize <= 0 || offset + 10 + frameSize > end) break;

        const fieldName = this.frameIds[frameId];
        if (fieldName === 'picture') {
          const picture = this.readPictureFrame(data, offset + 10, frameSize);
          if (picture) {
            result.picture = picture;
          }
        } else if (fieldName) {
          const content = this.readTextFrame(data, offset + 10, frameSize);
          if (content) {
            result[fieldName] = content;
          }
        }

        offset += 10 + frameSize;
      }

      return result;
    },

    syncsafeInt(data, offset) {
      return ((data[offset] & 0x7f) << 21) |
             ((data[offset + 1] & 0x7f) << 14) |
             ((data[offset + 2] & 0x7f) << 7) |
             (data[offset + 3] & 0x7f);
    },

    readTextFrame(data, offset, size) {
      if (size < 2) return '';
      
      const encoding = data[offset];
      let text = '';
      
      const start = offset + 1;
      const end = offset + size;
      
      if (encoding === 0 || encoding === 3) {
        // ISO-8859-1 or UTF-8
        for (let i = start; i < end; i++) {
          if (data[i] === 0) break;
          text += String.fromCharCode(data[i]);
        }
      } else if (encoding === 1 || encoding === 2) {
        // UTF-16 with or without BOM
        let i = start;
        if (data[i] === 0xFF && data[i + 1] === 0xFE) i += 2;
        else if (data[i] === 0xFE && data[i + 1] === 0xFF) i += 2;
        
        while (i < end - 1) {
          const char = data[i] | (data[i + 1] << 8);
          if (char === 0) break;
          text += String.fromCharCode(char);
          i += 2;
        }
      }
      
      return text.trim();
    },

    readPictureFrame(data, offset, size) {
      try {
        const encoding = data[offset];
        let pos = offset + 1;
        
        // Read MIME type (null-terminated)
        let mimeType = '';
        while (pos < offset + size && data[pos] !== 0) {
          mimeType += String.fromCharCode(data[pos]);
          pos++;
        }
        pos++; // Skip null terminator
        
        // Skip picture type byte
        pos++;
        
        // Skip description (null-terminated, encoding-dependent)
        if (encoding === 0 || encoding === 3) {
          while (pos < offset + size && data[pos] !== 0) pos++;
          pos++;
        } else {
          while (pos < offset + size - 1 && !(data[pos] === 0 && data[pos + 1] === 0)) pos += 2;
          pos += 2;
        }
        
        // Rest is image data
        const imageData = data.slice(pos, offset + size);
        
        if (imageData.length > 0) {
          // Convert to base64 data URL
          const base64 = this.arrayBufferToBase64(imageData);
          const mime = mimeType || 'image/jpeg';
          return `data:${mime};base64,${base64}`;
        }
      } catch (e) {
        console.warn('Failed to parse album art:', e);
      }
      return null;
    },

    arrayBufferToBase64(bytes) {
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    }
  };

  // DOM elements cache
  const elements = {
    inlinePlayer: null,
    footerPlayer: null,
    audio: null
  };

  // Initialize the music player system
  function init() {
    // Create global audio element
    createAudioElement();
    
    // Find players in DOM
    elements.inlinePlayer = document.querySelector('[data-music-player="inline"]');
    elements.footerPlayer = document.getElementById('MusicPlayerFooter');
    
    // Move footer player to body to escape any section wrappers
    if (elements.footerPlayer) {
      document.body.appendChild(elements.footerPlayer);
    }
    
    // Restore state from storage
    restoreState();
    
    // Setup inline player if present
    if (elements.inlinePlayer) {
      setupInlinePlayer(elements.inlinePlayer);
    }
    
    // Setup footer player
    if (elements.footerPlayer) {
      setupFooterPlayer(elements.footerPlayer);
    }
    
    // Handle page visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Save state before leaving page
    window.addEventListener('beforeunload', saveState);
    
    // Check if we should show footer player (navigated away from music article)
    checkFooterPlayerVisibility();
  }

  // Create the global audio element
  function createAudioElement() {
    // Check if audio element already exists
    let audio = document.getElementById('GlobalMusicPlayer');
    if (!audio) {
      audio = document.createElement('audio');
      audio.id = 'GlobalMusicPlayer';
      audio.preload = 'metadata';
      audio.style.display = 'none';
      document.body.appendChild(audio);
    }
    
    elements.audio = audio;
    state.audio = audio;
    
    // Audio event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);
    
    return audio;
  }

  // Setup inline player controls
  function setupInlinePlayer(player) {
    const audioUrl = player.dataset.audioUrl;
    const fallbackTitle = player.dataset.trackTitle;
    const fallbackArtist = player.dataset.artistName;
    const articleUrl = player.dataset.articleUrl;
    const coverImage = player.dataset.coverImage || '';
    
    // Store fallback track info (will be overwritten by ID3 if available)
    state.trackTitle = fallbackTitle;
    state.artistName = fallbackArtist;
    state.articleUrl = articleUrl;
    state.coverImage = coverImage;
    
    // Parse ID3 metadata from the MP3
    if (audioUrl && !state.id3Loaded) {
      loadID3Metadata(audioUrl, fallbackTitle, fallbackArtist, player);
    }
    
    // If this is the same track that was playing, sync state
    if (state.src === audioUrl && state.isPlaying) {
      state.activePlayer = 'inline';
      syncPlayerUI(player, true);
      hideFooterPlayer();
    } else if (state.src !== audioUrl) {
      // New track
      state.src = audioUrl;
      state.id3Loaded = false;
    }
    
    // Play button
    const playBtn = player.querySelector('[data-player-action="play"]');
    if (playBtn) {
      playBtn.addEventListener('click', () => togglePlay(audioUrl, player));
    }
    
    // Volume controls
    const muteBtn = player.querySelector('[data-player-action="mute"]');
    if (muteBtn) {
      muteBtn.addEventListener('click', toggleMute);
    }
    
    const volumeSlider = player.querySelector('[data-player-volume]');
    if (volumeSlider) {
      volumeSlider.value = state.volume * 100;
      volumeSlider.addEventListener('input', (e) => setVolume(e.target.value / 100));
    }
    
    // Progress slider
    const progressSlider = player.querySelector('[data-player-progress-slider]');
    if (progressSlider) {
      progressSlider.addEventListener('input', (e) => handleSliderSeek(e.target.value));
    }
    
    // Load audio if not already loaded
    if (elements.audio.src !== audioUrl) {
      elements.audio.src = audioUrl;
      elements.audio.load();
    }
    
    state.activePlayer = 'inline';
  }

  // Load and apply ID3 metadata
  async function loadID3Metadata(audioUrl, fallbackTitle, fallbackArtist, player) {
    try {
      const metadata = await ID3Parser.parse(audioUrl);
      
      if (metadata) {
        // Use ID3 data if available, fall back to provided values
        state.trackTitle = metadata.title || fallbackTitle;
        state.artistName = metadata.artist || fallbackArtist;
        state.albumName = metadata.album || '';
        state.year = metadata.year || '';
        state.id3Loaded = true;
        
        // Use album art from ID3 if available
        if (metadata.picture) {
          state.coverImage = metadata.picture;
        }
        
        // Update the inline player UI
        if (player) {
          updateInlinePlayerUI(player);
        }
        
        console.log('ID3 metadata loaded:', {
          title: state.trackTitle,
          artist: state.artistName,
          album: state.albumName,
          year: state.year,
          hasArt: !!metadata.picture
        });
      }
    } catch (e) {
      console.warn('Failed to load ID3 metadata:', e);
    }
  }

  // Update inline player with ID3 metadata
  function updateInlinePlayerUI(player) {
    const titleEl = player.querySelector('.music-player__track-title');
    const artistEl = player.querySelector('.music-player__artist');
    const coverContainer = player.querySelector('.music-player__cover');
    const coverPlaceholder = player.querySelector('.music-player__cover-placeholder');
    
    if (titleEl && state.trackTitle) {
      titleEl.textContent = state.trackTitle;
    }
    
    if (artistEl) {
      let artistText = state.artistName || '';
      if (state.albumName) {
        artistText += artistText ? ` \u2022 ${state.albumName}` : state.albumName;
      }
      if (state.year) {
        artistText += artistText ? ` (${state.year})` : state.year;
      }
      artistEl.textContent = artistText;
    }
    
    // Update cover image if we have album art from ID3
    if (state.coverImage && coverContainer) {
      let coverEl = coverContainer.querySelector('img');
      
      // Create img element if it doesn't exist (was placeholder only)
      if (!coverEl) {
        coverEl = document.createElement('img');
        coverEl.width = 60;
        coverEl.height = 60;
        coverEl.alt = state.trackTitle || '';
        coverContainer.insertBefore(coverEl, coverPlaceholder);
      }
      
      coverEl.src = state.coverImage;
      coverEl.style.display = 'block';
      
      if (coverPlaceholder) {
        coverPlaceholder.style.display = 'none';
      }
    }
  }

  // Setup footer player controls
  function setupFooterPlayer(player) {
    // Play buttons (main and mini)
    const playBtns = player.querySelectorAll('[data-player-action="play"]');
    playBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent bubbling to parent handlers
        togglePlay();
      });
    });
    
    // Volume controls
    const muteBtn = player.querySelector('[data-player-action="mute"]');
    if (muteBtn) {
      muteBtn.addEventListener('click', toggleMute);
    }
    
    const volumeSlider = player.querySelector('[data-footer-volume]');
    if (volumeSlider) {
      volumeSlider.value = state.volume * 100;
      volumeSlider.addEventListener('input', (e) => setVolume(e.target.value / 100));
    }
    
    // Progress slider
    const progressSlider = player.querySelector('[data-footer-progress-slider]');
    if (progressSlider) {
      progressSlider.addEventListener('input', (e) => handleSliderSeek(e.target.value));
    }
    
    // Minimize toggle
    const minimizeBtns = player.querySelectorAll('[data-player-action="toggle-minimize"]');
    minimizeBtns.forEach(btn => {
      btn.addEventListener('click', toggleMinimize);
    });
    
    // Close button
    const closeBtn = player.querySelector('[data-player-action="close"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', closePlayer);
    }
    
    // Link to article
    const link = player.querySelector('[data-footer-link]');
    if (link && state.articleUrl) {
      link.href = state.articleUrl;
    }
    
    // Initialize waveform
    const canvas = player.querySelector('[data-footer-waveform-canvas]');
    if (canvas) {
      initWaveform(canvas, 'footer');
    }
  }

  // Toggle minimize state
  function toggleMinimize() {
    if (!elements.footerPlayer) return;
    
    state.isMinimized = !state.isMinimized;
    elements.footerPlayer.classList.toggle('is-minimized', state.isMinimized);
    saveState();
  }

  // Toggle play/pause
  function togglePlay(src, player) {
    const audio = elements.audio;
    
    // If source provided and different from current, load new track
    if (src && audio.src !== src) {
      audio.src = src;
      audio.load();
      
      // Get track info from player if available
      if (player) {
        state.trackTitle = player.dataset.trackTitle || '';
        state.artistName = player.dataset.artistName || '';
        state.articleUrl = player.dataset.articleUrl || '';
        state.coverImage = player.dataset.coverImage || '';
        state.src = src;
      }
    }
    
    if (state.isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(handleError);
    }
  }

  // Toggle mute
  function toggleMute() {
    state.isMuted = !state.isMuted;
    elements.audio.muted = state.isMuted;
    updateMuteUI();
  }

  // Set volume
  function setVolume(value) {
    state.volume = Math.max(0, Math.min(1, value));
    elements.audio.volume = state.volume;
    
    // Update all volume sliders
    document.querySelectorAll('[data-player-volume], [data-footer-volume]').forEach(slider => {
      slider.value = state.volume * 100;
    });
  }

  // Handle progress bar click (waveform or progress bar)
  function handleProgressClick(e, progressBar) {
    const rect = progressBar.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = percent * state.duration;
    
    if (isFinite(time) && time >= 0 && time <= state.duration) {
      elements.audio.currentTime = time;
      state.currentTime = time;
      // Update all UI elements
      updateProgressUI();
      updateProgressSliders();
      updateMiniPlayer();
    }
  }

  // Handle slider seek
  function handleSliderSeek(value) {
    const percent = value / 100;
    const time = percent * state.duration;
    
    if (isFinite(time) && time >= 0 && time <= state.duration) {
      elements.audio.currentTime = time;
      state.currentTime = time;
      // Update all UI elements
      updateProgressUI();
      updateProgressSliders();
      updateMiniPlayer();
    }
  }

  // Handle LED strip click (legacy)
  function handleLEDClick(e, ledStrip) {
    const rect = ledStrip.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = percent * state.duration;
    
    if (isFinite(time) && time >= 0 && time <= state.duration) {
      elements.audio.currentTime = time;
      state.currentTime = time;
      // Update all UI elements
      updateProgressUI();
      updateProgressSliders();
      updateMiniPlayer();
    }
  }

  // Close the player
  function closePlayer() {
    elements.audio.pause();
    elements.audio.currentTime = 0;
    state.isPlaying = false;
    state.src = '';
    hideFooterPlayer();
    clearState();
    
    // Publish close event
    if (typeof publish === 'function') {
      publish('music:close', {});
    }
  }

  // Audio event handlers
  function handleLoadedMetadata() {
    state.duration = elements.audio.duration;
    updateDurationUI();
    
    // Restore playback position if applicable
    if (state.currentTime > 0 && state.currentTime < state.duration) {
      elements.audio.currentTime = state.currentTime;
    }
    
    // Generate waveform data
    generateWaveformData();
  }

  // Track last save time to avoid excessive localStorage writes
  let lastSaveTime = 0;
  
  function handleTimeUpdate() {
    state.currentTime = elements.audio.currentTime;
    updateProgressUI();
    updateProgressSliders();
    updateMiniPlayer();
    
    // Save state periodically (every 3 seconds) during playback
    const now = Date.now();
    if (state.isPlaying && now - lastSaveTime > 3000) {
      lastSaveTime = now;
      saveState();
    }
  }

  // Update mini player in restore tab
  function updateMiniPlayer() {
    const miniTime = document.querySelector('[data-mini-time]');
    const miniTitle = document.querySelector('[data-mini-title]');
    const marqueeContainer = document.querySelector('.music-player-footer__mini-marquee');
    
    if (miniTime) {
      miniTime.textContent = formatTime(state.currentTime);
    }
    
    if (miniTitle) {
      // Format as "Artist - Song Name"
      let displayText = '';
      if (state.artistName && state.trackTitle) {
        displayText = `${state.artistName} - ${state.trackTitle}`;
      } else if (state.trackTitle) {
        displayText = state.trackTitle;
      } else {
        displayText = 'Now Playing';
      }
      
      miniTitle.textContent = displayText;
      
      // Check if text needs to scroll
      if (marqueeContainer && miniTitle) {
        const containerWidth = marqueeContainer.offsetWidth;
        const textWidth = miniTitle.scrollWidth;
        
        if (textWidth > containerWidth) {
          miniTitle.classList.add('needs-scroll');
          // Set animation duration based on text length
          const duration = Math.max(5, textWidth / 30);
          miniTitle.style.animationDuration = `${duration}s`;
        } else {
          miniTitle.classList.remove('needs-scroll');
          miniTitle.style.animationDuration = '';
        }
      }
    }
  }

  function handleEnded() {
    state.isPlaying = false;
    state.currentTime = 0;
    updatePlayButtonUI();
    hideFooterPlayer();
    clearState();
    
    if (typeof publish === 'function') {
      publish('music:ended', { trackTitle: state.trackTitle });
    }
  }

  function handlePlay() {
    state.isPlaying = true;
    state.pendingPlay = false;
    updatePlayButtonUI();
    removeAutoplayPrompt(); // Remove the TAP indicator
    saveState(); // Persist playing state
    
    if (typeof publish === 'function') {
      publish('music:play', { 
        trackTitle: state.trackTitle,
        artistName: state.artistName
      });
    }
  }

  function handlePause() {
    state.isPlaying = false;
    updatePlayButtonUI();
    saveState();
    
    if (typeof publish === 'function') {
      publish('music:pause', { trackTitle: state.trackTitle });
    }
  }

  function handleError(e) {
    console.error('Music Player Error:', e);
    state.isPlaying = false;
    updatePlayButtonUI();
  }

  function handleVisibilityChange() {
    if (document.hidden && state.isPlaying) {
      saveState();
    }
  }

  // UI update functions
  function updatePlayButtonUI() {
    const allPlayButtons = document.querySelectorAll('[data-player-action="play"]');
    allPlayButtons.forEach(btn => {
      const player = btn.closest('[data-music-player]');
      if (player) {
        player.classList.toggle('is-playing', state.isPlaying);
      }
    });
  }

  function updateMuteUI() {
    const allMuteButtons = document.querySelectorAll('[data-player-action="mute"]');
    allMuteButtons.forEach(btn => {
      const player = btn.closest('[data-music-player]');
      if (player) {
        player.classList.toggle('is-muted', state.isMuted);
      }
    });
  }

  function updateProgressUI() {
    const progress = state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0;
    
    // Update all progress fills
    document.querySelectorAll('[data-player-progress-fill], [data-footer-progress-fill]').forEach(fill => {
      fill.style.width = `${progress}%`;
    });
    
    // Update time displays
    const currentFormatted = formatTime(state.currentTime);
    const durationFormatted = formatTime(state.duration);
    
    document.querySelectorAll('[data-player-time-current], [data-footer-time-current]').forEach(el => {
      el.textContent = currentFormatted;
    });
    
    document.querySelectorAll('[data-player-time-duration], [data-footer-time-duration]').forEach(el => {
      el.textContent = durationFormatted;
    });
  }

  function updateDurationUI() {
    const durationFormatted = formatTime(state.duration);
    document.querySelectorAll('[data-player-time-duration], [data-footer-time-duration]').forEach(el => {
      el.textContent = durationFormatted;
    });
  }

  function syncPlayerUI(player, isPlaying) {
    player.classList.toggle('is-playing', isPlaying);
    updateProgressUI();
  }

  function updateFooterPlayerInfo() {
    if (!elements.footerPlayer) return;
    
    const track = elements.footerPlayer.querySelector('[data-footer-track]');
    const artist = elements.footerPlayer.querySelector('[data-footer-artist]');
    const link = elements.footerPlayer.querySelector('[data-footer-link]');
    const cover = elements.footerPlayer.querySelector('[data-footer-cover]');
    const placeholder = elements.footerPlayer.querySelector('.music-player-footer__cover-placeholder');
    
    if (track) track.textContent = state.trackTitle || 'Unknown Track';
    
    if (artist) {
      let artistText = state.artistName || 'Unknown Artist';
      if (state.albumName) {
        artistText += ` \u2022 ${state.albumName}`;
      }
      if (state.year) {
        artistText += ` (${state.year})`;
      }
      artist.textContent = artistText;
    }
    
    if (link) link.href = state.articleUrl || '#';
    
    // Update cover image
    if (cover) {
      if (state.coverImage) {
        cover.src = state.coverImage;
        cover.alt = state.trackTitle || '';
        cover.classList.add('has-image');
        if (placeholder) placeholder.style.display = 'none';
      } else {
        cover.classList.remove('has-image');
        if (placeholder) placeholder.style.display = 'flex';
      }
    }
    
    // Also update mini player title
    const miniTitle = document.querySelector('[data-mini-title]');
    if (miniTitle) {
      let displayText = '';
      if (state.artistName && state.trackTitle) {
        displayText = `${state.artistName} - ${state.trackTitle}`;
      } else if (state.trackTitle) {
        displayText = state.trackTitle;
      } else {
        displayText = 'Now Playing';
      }
      miniTitle.textContent = displayText;
    }
  }

  // Update all progress sliders
  function updateProgressSliders() {
    const progress = state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0;
    
    // Update all progress sliders
    const sliders = document.querySelectorAll('[data-player-progress-slider], [data-footer-progress-slider]');
    sliders.forEach(slider => {
      slider.value = progress;
      // Update CSS custom property for styling the filled portion
      slider.style.setProperty('--progress', `${progress}%`);
    });
  }

  // Footer player visibility
  function showFooterPlayer() {
    if (!elements.footerPlayer || !state.src) return;
    
    updateFooterPlayerInfo();
    elements.footerPlayer.style.display = 'block';
    elements.footerPlayer.setAttribute('aria-hidden', 'false');
    state.activePlayer = 'footer';
    
    // Restore minimized state
    elements.footerPlayer.classList.toggle('is-minimized', state.isMinimized);
    
    // Add body padding to prevent content from being hidden (less if minimized)
    document.body.style.paddingBottom = state.isMinimized ? '0px' : '70px';
    
    // Sync UI state
    elements.footerPlayer.classList.toggle('is-playing', state.isPlaying);
    updateProgressUI();
    updateProgressSliders();
    
    // Update mini player after footer is visible (for width calculations)
    requestAnimationFrame(() => {
      updateMiniPlayer();
    });
  }

  function hideFooterPlayer() {
    if (!elements.footerPlayer) return;
    
    elements.footerPlayer.style.display = 'none';
    elements.footerPlayer.setAttribute('aria-hidden', 'true');
    document.body.style.paddingBottom = '';
  }

  function checkFooterPlayerVisibility() {
    // If there's a stored state with a playing track and we're not on the article page
    const stored = getStoredState();
    if (!stored || !stored.src) return;
    
    // Check if we're on the same article
    const isOnMusicArticle = elements.inlinePlayer !== null;
    
    if (isOnMusicArticle) {
      // We're on a music article - inline player handles it
      hideFooterPlayer();
      
      // If it's the same track that was playing, continue
      if (stored.src === elements.inlinePlayer.dataset.audioUrl) {
        resumePlayback(stored, elements.inlinePlayer);
      }
    } else if (stored.isPlaying || stored.currentTime > 0) {
      // Not on music article but track was playing - show footer
      Object.assign(state, stored);
      showFooterPlayer();
      resumePlayback(stored, null);
    }
  }

  // Resume playback with proper audio loading
  function resumePlayback(stored, playerElement) {
    const shouldPlay = stored.isPlaying;
    
    // Set source if different
    if (elements.audio.src !== stored.src) {
      elements.audio.src = stored.src;
    }
    
    elements.audio.volume = stored.volume || 0.8;
    
    // Track if we need user interaction
    state.pendingPlay = shouldPlay;
    
    // Wait for audio to be ready before seeking and playing
    const onCanPlay = () => {
      elements.audio.removeEventListener('canplay', onCanPlay);
      
      // Seek to stored position
      if (stored.currentTime > 0 && stored.currentTime < (elements.audio.duration || Infinity)) {
        elements.audio.currentTime = stored.currentTime;
      }
      
      // Update state
      state.currentTime = stored.currentTime;
      state.volume = stored.volume;
      updateProgressUI();
      updateProgressSliders();
      
      // Resume playing if it was playing
      if (shouldPlay) {
        elements.audio.play().then(() => {
          state.isPlaying = true;
          state.pendingPlay = false;
          updatePlayButtonUI();
          removeAutoplayPrompt();
          if (playerElement) {
            syncPlayerUI(playerElement, true);
          }
        }).catch((e) => {
          console.log('Autoplay prevented, user interaction required:', e.message);
          // Show prompt that user needs to tap play
          state.isPlaying = false;
          state.pendingPlay = true;
          updatePlayButtonUI();
          showAutoplayPrompt();
        });
      }
    };
    
    // Check if already loaded enough
    if (elements.audio.readyState >= 3) {
      onCanPlay();
    } else {
      elements.audio.addEventListener('canplay', onCanPlay);
      elements.audio.load();
    }
  }
  
  // Show visual prompt when autoplay is blocked
  function showAutoplayPrompt() {
    if (!elements.footerPlayer) return;
    
    elements.footerPlayer.classList.add('autoplay-blocked');
    
    // Add pulsing effect to play button
    const playBtn = elements.footerPlayer.querySelector('.music-player-footer__btn--play');
    if (playBtn) {
      playBtn.classList.add('needs-tap');
    }
  }
  
  function removeAutoplayPrompt() {
    if (!elements.footerPlayer) return;
    
    elements.footerPlayer.classList.remove('autoplay-blocked');
    
    const playBtn = elements.footerPlayer.querySelector('.music-player-footer__btn--play');
    if (playBtn) {
      playBtn.classList.remove('needs-tap');
    }
  }

  // Waveform visualization
  function initWaveform(canvas, type) {
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.parentElement.offsetWidth || 200;
    const height = canvas.parentElement.offsetHeight || 40;
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw placeholder waveform
    drawPlaceholderWaveform(ctx, width, height);
  }

  function generateWaveformData() {
    // Generate pseudo-random waveform data based on duration
    // In a real implementation, you'd analyze the actual audio
    const bars = 50;
    state.waveformData = [];
    
    for (let i = 0; i < bars; i++) {
      // Create natural-looking variation
      const base = 0.3 + Math.random() * 0.4;
      const peak = Math.sin(i * 0.3) * 0.2;
      state.waveformData.push(Math.min(1, Math.max(0.1, base + peak)));
    }
    
    // Redraw waveforms
    document.querySelectorAll('[data-player-waveform], [data-footer-waveform-canvas]').forEach(canvas => {
      drawWaveform(canvas);
    });
  }

  function drawPlaceholderWaveform(ctx, width, height) {
    const bars = 50;
    const barWidth = width / bars - 1;
    const centerY = height / 2;
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    
    for (let i = 0; i < bars; i++) {
      const barHeight = (0.2 + Math.random() * 0.3) * height;
      const x = i * (barWidth + 1);
      const y = centerY - barHeight / 2;
      
      ctx.fillRect(x, y, barWidth, barHeight);
    }
  }

  function drawWaveform(canvas) {
    if (!canvas || !state.waveformData) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    if (width === 0 || height === 0) return;
    
    const bars = state.waveformData.length;
    const barWidth = Math.max(2, (width / bars) - 1);
    const centerY = height / 2;
    
    ctx.clearRect(0, 0, width, height);
    
    const progress = state.duration > 0 ? state.currentTime / state.duration : 0;
    const progressX = progress * width;
    
    // Get theme color from CSS variable or use fallback
    const activeColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-button').trim() || '0, 128, 0';
    
    state.waveformData.forEach((value, i) => {
      const barHeight = Math.max(4, value * height * 0.8);
      const x = i * (barWidth + 1);
      const y = centerY - barHeight / 2;
      
      // Color based on progress
      if (x < progressX) {
        ctx.fillStyle = `rgba(${activeColor}, 0.95)`; // Active/played
      } else {
        ctx.fillStyle = 'rgba(128, 128, 128, 0.35)'; // Unplayed - gray
      }
      
      ctx.fillRect(x, y, barWidth, barHeight);
    });
  }

  // State persistence
  function saveState() {
    if (!state.src) return;
    
    const data = {
      src: state.src,
      currentTime: state.currentTime,
      duration: state.duration,
      isPlaying: state.isPlaying,
      isMinimized: state.isMinimized,
      volume: state.volume,
      trackTitle: state.trackTitle,
      artistName: state.artistName,
      albumName: state.albumName,
      year: state.year,
      articleUrl: state.articleUrl,
      coverImage: state.coverImage
    };
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('Could not save music player state:', e);
    }
  }

  function getStoredState() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }

  function restoreState() {
    const stored = getStoredState();
    if (stored) {
      Object.assign(state, stored);
      // Ensure isMinimized has a valid boolean value (for old stored data)
      if (typeof state.isMinimized !== 'boolean') {
        state.isMinimized = false;
      }
    }
  }

  function clearState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // Ignore
    }
  }

  // Utility functions
  function formatTime(seconds) {
    if (!isFinite(seconds)) return '0:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.MusicPlayer = {
    play: togglePlay,
    pause: () => elements.audio?.pause(),
    setVolume,
    getState: () => ({ ...state })
  };
})();

